'use client'

import { motion } from 'framer-motion'
import { Building2, Shield, Zap, ArrowRight } from 'lucide-react'

export default function Hero() {
  return (
    <section className="pt-24 pb-16 px-4 overflow-hidden">
      <div className="container mx-auto max-w-6xl">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-8"
          >
            <div className="space-y-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="inline-flex items-center space-x-2 bg-gradient-to-r from-primary-100 to-secondary-100 px-4 py-2 rounded-full"
              >
                <Building2 className="w-4 h-4 text-primary-600" />
                <span className="text-sm font-medium text-primary-700">Innovative Technology Solutions</span>
              </motion.div>
              
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="text-4xl md:text-6xl font-bold leading-tight"
              >
                Building the Future of{' '}
                <span className="gradient-text">Digital Education</span>{' '}
                & Safety
              </motion.h1>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="text-xl text-gray-600 leading-relaxed"
              >
                Elyndra Studios Ltd builds Sparky Ventures, a business-skills platform for families, in development (beta planned), and Rebel Verify, privacy-first age verification in development.
              </motion.p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <motion.a
                href="#products"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <ArrowRight className="w-5 h-5 mr-2" />
                Explore Our Products
              </motion.a>
              
              <motion.a
                href="#about"
                className="border-2 border-primary-200 text-primary-700 font-semibold py-4 px-8 rounded-2xl hover:bg-primary-50 transition-all duration-200 inline-flex items-center"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Building2 className="w-5 h-5 mr-2" />
                Learn More
              </motion.a>
            </motion.div>

            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.2 }}
              className="flex items-center space-x-8 pt-4"
            >
              <div className="flex items-center space-x-2">
                <Shield className="w-5 h-5 text-green-500" />
                <span className="text-gray-600 font-medium">Privacy-First</span>
              </div>
              <div className="flex items-center space-x-2">
                <Zap className="w-5 h-5 text-yellow-500" />
                <span className="text-gray-600 font-medium">Cutting-Edge Tech</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Content - Corporate Illustration */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative"
          >
            <div className="relative z-10">
              {/* Main Corporate Dashboard */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                className="bg-gradient-to-br from-primary-500 to-secondary-500 rounded-3xl p-8 shadow-2xl"
              >
                <div className="bg-white rounded-2xl p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-gray-800">Elyndra Platform</h3>
                    <div className="w-3 h-3 bg-orange-400 rounded-full"></div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gradient-to-br from-blue-100 to-cyan-100 rounded-xl p-3">
                      <Building2 className="w-6 h-6 text-blue-600 mb-2" />
                      <p className="text-xs font-medium text-gray-700">Sparky Ventures</p>
                      <p className="text-xs text-orange-600">Marketplace Platform</p>
                    </div>
                    <div className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-xl p-3">
                      <Shield className="w-6 h-6 text-purple-600 mb-2" />
                      <p className="text-xs font-medium text-gray-700">RebelVerify</p>
                      <p className="text-xs text-orange-600">Age Verification</p>
                    </div>
                  </div>
                  
                </div>
              </motion.div>
            </div>

            {/* Floating Tech Elements */}
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="absolute -top-4 -right-4 w-16 h-16 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-full flex items-center justify-center shadow-lg"
            >
              <Zap className="w-8 h-8 text-white" />
            </motion.div>

            <motion.div
              animate={{ y: [0, -15, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              className="absolute -bottom-4 -left-4 w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-400 rounded-full flex items-center justify-center shadow-lg"
            >
              <Shield className="w-6 h-6 text-white" />
            </motion.div>

            <motion.div
              animate={{ x: [0, 10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 2 }}
              className="absolute top-1/2 -right-8 w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center shadow-lg"
            >
              <Building2 className="w-5 h-5 text-white" />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
